import {
  
  ClipboardPlus,
  
} from "lucide-react";

const ICONS = "/assets/images/plan-icons/";

export function isCustomIcon(icon) {
  return typeof icon === "string";
}

/**
 * Renders a benefit's icon inside a tinted badge.
 * Handles both custom image assets (icon is a string path) and
 * lucide-react components (icon is a component reference).
 */
export function BenefitIcon({ config, size = 44, className = "" }) {
  const { icon, tint } = config;
  const badgeStyle = {
    width: size,
    height: size,
    borderRadius: "0.75rem",
    backgroundColor: tint.bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  if (isCustomIcon(icon)) {
    return (
      <div style={badgeStyle} className={className}>
        <img
          src={icon}
          alt=""
          width={size * 0.9}
          height={size * 0.9}
          style={{ objectFit: "contain" }}
        />
      </div>
    );
  }

  const Icon = icon;
  return (
    <div style={badgeStyle} className={className}>
      <Icon size={size * 0.45} color={tint.fg} strokeWidth={2} />
    </div>
  );
}

const TINT = {
  teal: { bg: "#E6F6F4", fg: "#0F9488" },
  blue: { bg: "#E8F1FE", fg: "#2563EB" },
  violet: { bg: "#F1EBFC", fg: "#6D28D9" },
  amber: { bg: "#FDF3E3", fg: "#B45309" },
  rose: { bg: "#FCEAEF", fg: "#BE123C" },
  green: { bg: "#E9F7EE", fg: "#15803D" },
  slate: { bg: "#F1F1F4", fg: "#475569" },
  pink: { bg: "#FCEAFE", fg: "#A21CAF" },
};

export const BENEFIT_CARD_CONFIG = {
  Diagnostic: {
    icon: ICONS + "diagnostic.png", 
    tint: TINT.teal,
    tagline: "Quality diagnostic tests and preventive screenings.",
    highlights: [
      "Quality diagnostic tests",
      "Partner lab network",
      "Affordable healthcare support",
    ],
  },
  Scans: {
    icon: ICONS + "scans.png", 
    tint: TINT.blue,
    tagline: "Imaging and scans at trusted diagnostic centres.",
    highlights: ["Wide scan coverage", "Partner radiology network", "Fast report turnaround"],
  },
  Pharmacy: {
    icon: ICONS + "pharmacy.png", 
    tint: TINT.violet,
    tagline: "Affordable access to prescribed medicines.",
    highlights: [
      "Medicine purchase support",
      "Prescription-based benefits",
      "Network pharmacy access",
    ],
  },
  "In Clinic Consultation": {
    icon: ICONS + "building.png", 
    tint: TINT.violet,
    tagline: "Visit specialists across a trusted clinic network.",
    highlights: ["Wide specialist network", "In-person consultations", "Easy appointment booking"],
  },
  "Tele Consultation": {
    icon: ICONS + "teleconsultation.png", 
    tint: TINT.amber,
    tagline: "Connect with doctors from anywhere, anytime.",
    highlights: ["24x7 doctor access", "No travel required", "Digital prescriptions"],
  },
  "Video Consultation": {
    icon:  ICONS + "teleconsultation.png",
    tint: TINT.amber,
    tagline: "Face-to-face consultations from the comfort of home.",
    highlights: ["Live video consults", "Certified doctors", "Digital prescriptions"],
  },
  "Doctor Consultation": {
    icon: ICONS + "stethoscope.png", 
    tint: TINT.violet,
    tagline: "Expert medical advice whenever you need it.",
    highlights: ["Verified doctors", "Multiple specialities", "Flexible scheduling"],
  },
  Dental: {
    icon: ICONS + "dentalcare.png", 
    tint: TINT.amber,
    tagline: "Complete oral care from checkups to treatments.",
    highlights: ["Routine dental checkups", "Partner dental clinics", "Preventive oral care"],
  },
  "Dental and Skin Care": {
    icon: ICONS + "smile.png", 
    tint: TINT.amber,
    tagline: "Dental and dermatology care under one benefit.",
    highlights: ["Dental treatments", "Skin & aesthetic care", "Partner clinic network"],
  },
  "Vision Care": {
    icon: ICONS + "visioncare.png", 
    tint: TINT.rose,
    tagline: "Comprehensive eye checkups and vision support.",
    highlights: ["Regular eye checkups", "Vision correction support", "Partner optical network"],
  },
  Vaccination: {
    icon: ICONS + "vaccination.png", 
    tint: TINT.green,
    tagline: "Stay protected with timely vaccinations.",
    highlights: ["Wide vaccine coverage", "Certified vaccination centres", "Preventive care"],
  },
  "Maternity Care": {
    icon: ICONS + "maternitycare.png", 
    tint: TINT.pink,
    tagline: "Dedicated support through pregnancy and beyond.",
    highlights: ["Prenatal & postnatal care", "Specialist consultations", "Maternity checkups"],
  },
  "Diabetic Care": {
    icon: ICONS + "diabeticcare.png", 
    tint: TINT.rose,
    tagline: "Ongoing support to help you manage diabetes.",
    highlights: ["Regular monitoring", "Specialist guidance", "Condition management programs"],
  },
  Naturopathy: {
    icon: ICONS + "naturopathy.png", 
    tint: TINT.green,
    tagline: "Holistic wellness through natural therapies.",
    highlights: ["Yoga & meditation", "Therapeutic treatments", "Holistic wellness programs"],
  },
  Gym: {
    icon: ICONS + "dumbbell.png", 
    tint: TINT.amber,
    tagline: "Stay fit with access to partner gyms.",
    highlights: ["Gym memberships", "Partner fitness centres", "Flexible plans"],
  },
  Fitness: {
    icon: ICONS + "dumbbell.png", 
    tint: TINT.rose,
    tagline: "Fitness classes and sessions to keep you active.",
    highlights: ["Group fitness classes", "Certified trainers", "Flexible scheduling"],
  },
  "Fitness Studio": {
    icon: ICONS + "dumbbell.png", 
    tint: TINT.amber,
    tagline: "Curated fitness sessions at partner studios.",
    highlights: ["Studio-based sessions", "Variety of formats", "Partner network"],
  },
  "Mental Wellness": {
    icon: ICONS + "annualhealthcare.png", 
    tint: TINT.violet,
    tagline: "Support for a healthier, calmer mind.",
    highlights: ["Therapy & counselling", "Stress management", "Confidential support"],
  },
  EAP: {
    icon: ICONS + "eap.png", 
    tint: TINT.slate,
    tagline: "Confidential employee assistance and support.",
    highlights: ["Emotional wellbeing support", "Confidential counselling", "Habit & lifestyle coaching"],
  },
  "Weight Management": {
    icon: ICONS + "weightmanagement.png", 
    tint: TINT.green,
    tagline: "Structured guidance for sustainable weight goals.",
    highlights: ["Personalized plans", "Expert guidance", "Progress tracking"],
  },
  "Pet Care": {
    icon: ICONS + "petcare.png",
    tint: TINT.green,
    tagline: "Care and coverage for your furry companions.",
    highlights: ["Vet consultations", "Pet health support", "Partner pet clinics"],
  },
  Ambulance: {
    icon: ICONS + "ambulance.png", 
    tint: TINT.rose,
    tagline: "Emergency transport support when it matters most.",
    highlights: ["24x7 availability", "Emergency response", "Wide coverage network"],
  },
  Insurance: {
    icon: ICONS + "Shield.png",
    tint: TINT.blue,
    tagline: "Financial protection for you and your family.",
    highlights: ["Comprehensive coverage", "Trusted insurers", "Peace of mind"],
  },
  "Insurance Dummy": {
    icon: ICONS + "Shield.png", 
    tint: TINT.blue,
    tagline: "Financial protection for you and your family.",
    highlights: ["Comprehensive coverage", "Trusted insurers", "Peace of mind"],
  },
  AHC: {
    icon: ICONS + "annualhealthcare.png", 
    tint: TINT.teal,
    tagline: "Annual health checkups to stay ahead of risks.",
    highlights: ["Comprehensive checkups", "Preventive screening", "Early risk detection"],
  },
  "Health Assessment": {
    icon: ICONS + "annualhealthcare.png", 
    tint: TINT.teal,
    tagline: "A complete picture of your current health.",
    highlights: ["Detailed assessments", "Preventive insights", "Personalized reports"],
  },
  "Report Review Consultation": {
    icon: ICONS + "annualhealthcare.png",
    tint: TINT.teal,
    tagline: "Expert review and explanation of your reports.",
    highlights: ["Specialist report review", "Personalized explanation", "Follow-up guidance"],
  },
  Dietician: {
    icon: ICONS + "aidietplanner.png", 
    tint: TINT.green,
    tagline: "Personalized nutrition guidance from experts.",
    highlights: ["Custom diet plans", "Ongoing nutrition support", "Certified dieticians"],
  },
  DIET: {
    icon: ICONS + "aidietplanner.png", 
    tint: TINT.green,
    tagline: "Personalized nutrition guidance from experts.",
    highlights: ["Custom diet plans", "Ongoing nutrition support", "Certified dieticians"],
  },
  "Chat Bot": {
    icon: ICONS + "chatbot.png", 
    tint: TINT.blue,
    tagline: "Instant answers to your health questions.",
    highlights: ["Round-the-clock access", "Quick health guidance", "Easy escalation to doctors"],
  },
  Membership: {
    icon: ICONS + "membership.png", // recolored to TINT.slate
    tint: TINT.slate,
    tagline: "Exclusive access through partner memberships.",
    highlights: ["Partner brand access", "Member-only perks", "Flexible validity"],
  },
  "Healthcare Gadgets": {
    icon: ICONS + "healthcaregadget.png", 
    tint: TINT.slate,
    tagline: "Smart devices to help you track your health.",
    highlights: ["Health tracking devices", "Partner gadget brands", "Everyday wellness support"],
  },
  "Condition Management": {
    icon: ICONS + "conditionmanagement.svg",
    tint: TINT.green,
    tagline: "Structured support for managing health conditions.",
    highlights: ["Personalized care plans", "Ongoing monitoring", "Specialist guidance"],
  },
  ABDM: {
    icon: ICONS + "fileheart.png", 
    tint: TINT.slate,
    tagline: "Digitally manage and access your health records.",
    highlights: ["Digital health records", "Secure data access", "ABDM-compliant"],
  },
  HRA: {
    icon: ICONS + "building.png",
    tint: TINT.slate,
    tagline: "Support for your health risk assessment needs.",
    highlights: ["Risk assessment support", "Personalized insights", "Preventive focus"],
  },
  Reminder: {
    icon: ICONS + "reminder.png", 
    tint: TINT.amber,
    tagline: "Timely nudges to stay on top of your health.",
    highlights: ["Scheduled reminders", "Never miss a checkup", "Simple notifications"],
  },
  "Elder Care": {
    icon: ICONS + "eldercare.png", 
    tint: TINT.amber,
    tagline: "Dedicated healthcare support for elderly family members.",
    highlights: ["Specialist elder care", "Home-friendly support", "Ongoing monitoring"],
  },
};

export const DEFAULT_BENEFIT_CONFIG = {
  icon: ClipboardPlus,
  tint: TINT.slate,
  tagline: "Explore this benefit and what it covers.",
  highlights: ["Curated healthcare support", "Verified partner network", "Simple, transparent access"],
};

/** Resolve config by serviceName, falling back to the default. */
export function getBenefitCardConfig(serviceName) {
  return BENEFIT_CARD_CONFIG[serviceName] || DEFAULT_BENEFIT_CONFIG;
}